import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { getListing, trackWhatsAppClick, trackListingView } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import MetaTags from '../components/MetaTags';
import NotFoundPage from './NotFoundPage';
import { 
  MapPin, Wifi, Wind, Car, Users, Star, Eye, MessageCircle, 
  ArrowLeft, Check, Home, Loader2, Phone, X, ChevronLeft, ChevronRight, Play,
  Share2, Copy, Facebook, Twitter
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const ListingDetailPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showGallery, setShowGallery] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);

  const defaultImages = [
    "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800",
    "https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=800",
    "https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800"
  ];

  const images = listing?.images?.length > 0 ? listing.images : defaultImages;
  // Video as first "media slide" in carousel (Airbnb-style)
  const hasVideo = !!listing?.video_url;
  const mediaItems = hasVideo
    ? [{ type: 'video', url: listing.video_url, thumb: listing.video_thumbnail, duration: listing.video_duration }, ...images.map(u => ({ type: 'image', url: u }))]
    : images.map(u => ({ type: 'image', url: u }));

  useEffect(() => {
    loadListing();
  }, [id]);

  // Handle keyboard navigation for gallery
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showGallery) return;
      if (e.key === 'ArrowRight') setSelectedImageIndex((prev) => (prev + 1) % images.length);
      if (e.key === 'ArrowLeft') setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
      if (e.key === 'Escape') setShowGallery(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showGallery, images.length]);

  const loadListing = async () => {
    try {
      const res = await getListing(id);
      setListing(res.data);
    } catch (error) {
      console.error('Error loading listing:', error);
    } finally {
      setLoading(false);
    }
  };

  const typeLabels = {
    apartment: t('apartment'),
    room: t('room'),
    hotel: t('hotel'),
    studio: t('studio'),
  };

  const getWhatsAppLink = () => {
    if (!listing) return '#';
    const message = encodeURIComponent(
      language === 'fr'
        ? `Bonjour, je vous contacte via Roomia.net pour le logement situé à ${listing.neighborhood}, ${listing.city}`
        : `Hello, I'm contacting you via Roomia.net about the accommodation in ${listing.neighborhood}, ${listing.city}`
    );
    return `https://wa.me/${listing.whatsapp?.replace('+', '')}?text=${message}`;
  };

  // Track WhatsApp click
  const handleWhatsAppClick = () => {
    if (listing?.listing_id) {
      trackWhatsAppClick(listing.listing_id).catch(err => console.log('Track error:', err));
    }
  };

  // === SOCIAL SHARING =====================================================
  // We share the OG-rich URL `/api/share/listings/<id>` (NOT the SPA route),
  // built off the current origin so Facebook/WhatsApp/Twitter crawlers fetch
  // the listing-specific OpenGraph tags (title, photo, price) and redirect
  // real users to the SPA detail page automatically.
  const getShareUrl = () => {
    if (!listing) return '';
    const origin = (typeof window !== 'undefined' && window.location?.origin) || BACKEND;
    return `${origin}/api/share/listings/${listing.listing_id}`;
  };

  const getShareText = () => {
    if (!listing) return '';
    return language === 'fr'
      ? `${listing.title} — ${listing.neighborhood}, ${listing.city} sur Roomia.net`
      : `${listing.title} — ${listing.neighborhood}, ${listing.city} on Roomia.net`;
  };

  const handleNativeShare = async () => {
    const url = getShareUrl();
    const text = getShareText();
    if (navigator.share) {
      try {
        await navigator.share({ title: listing.title, text, url });
        return;
      } catch (_err) {
        // user dismissed share sheet — fall through to dropdown
      }
    }
    setShareMenuOpen((v) => !v);
  };

  const handleCopyLink = async () => {
    const url = getShareUrl();
    try {
      await navigator.clipboard.writeText(url);
      toast.success(language === 'fr' ? 'Lien copié !' : 'Link copied!');
    } catch (_err) {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      toast.success(language === 'fr' ? 'Lien copié !' : 'Link copied!');
    }
    setShareMenuOpen(false);
  };

  const openShareWindow = (url) => {
    window.open(url, '_blank', 'width=600,height=600,noopener,noreferrer');
    setShareMenuOpen(false);
  };

  const shareToFacebook = () => {
    openShareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getShareUrl())}`);
  };

  const shareToTwitter = () => {
    openShareWindow(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(getShareText())}&url=${encodeURIComponent(getShareUrl())}`
    );
  };

  const shareToWhatsApp = () => {
    openShareWindow(
      `https://wa.me/?text=${encodeURIComponent(`${getShareText()} ${getShareUrl()}`)}`
    );
  };
  // ========================================================================


  // Track listing view
  useEffect(() => {
    if (listing?.listing_id) {
      trackListingView(listing.listing_id).catch(err => console.log('Track error:', err));
    }
  }, [listing?.listing_id]);

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!listing) {
    // Show a proper 404 page instead of empty body
    return <NotFoundPage />;
  }

  // Build absolute image URL for OG tags (Facebook/WhatsApp crawlers)
  const ogImage = (() => {
    const firstImg = listing.images?.[0];
    if (!firstImg) return undefined;
    if (firstImg.startsWith('http')) return firstImg;
    return `${BACKEND}${firstImg}`;
  })();

  const ogDescription = listing.description
    ? listing.description.slice(0, 160).replace(/\s+\S*$/, '') + (listing.description.length > 160 ? '…' : '')
    : `${listing.listing_type === 'apartment' ? 'Appartement' : listing.listing_type} meublé à ${listing.neighborhood}, ${listing.city}. ${Math.round(listing.price_per_night).toLocaleString('fr-FR')} FCFA/nuit.`;

  return (
    <div className="min-h-screen pb-20">
      <MetaTags
        title={`${listing.title} — ${listing.neighborhood}, ${listing.city}`}
        description={ogDescription}
        image={ogImage}
        type="article"
      />
      {/* Fullscreen Gallery Modal - Mobile Optimized */}
      {showGallery && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          {/* Header */}
          <div className="p-4 flex items-center justify-between text-white shrink-0">
            <div>
              <h3 className="font-semibold text-base md:text-lg line-clamp-1">{listing.title}</h3>
              <p className="text-sm text-white/70">{selectedImageIndex + 1} / {images.length}</p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/10"
              onClick={() => setShowGallery(false)}
            >
              <X className="h-6 w-6" />
            </Button>
          </div>

          {/* Main Image Area - Full width on mobile */}
          <div className="flex-1 flex items-center justify-center relative overflow-hidden">
            {/* Previous Button — branded & high visibility */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-3 md:left-6 h-12 w-12 md:h-14 md:w-14 rounded-full bg-white text-primary shadow-xl ring-1 ring-black/10 hover:bg-primary hover:text-white hover:scale-110 transition-all z-10"
              onClick={prevImage}
              aria-label="Image précédente"
              data-testid="gallery-prev-btn"
            >
              <ChevronLeft className="h-6 w-6 md:h-7 md:w-7" strokeWidth={2.5} />
            </Button>

            {/* Main Image - Full width on mobile */}
            <img
              src={images[selectedImageIndex]}
              alt={`${listing.title} - ${selectedImageIndex + 1}`}
              className="w-full h-full object-contain px-2 md:px-16"
              onError={(e) => { e.target.src = defaultImages[0]; }}
            />

            {/* Next Button — branded & high visibility */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-3 md:right-6 h-12 w-12 md:h-14 md:w-14 rounded-full bg-white text-primary shadow-xl ring-1 ring-black/10 hover:bg-primary hover:text-white hover:scale-110 transition-all z-10"
              onClick={nextImage}
              aria-label="Image suivante"
              data-testid="gallery-next-btn"
            >
              <ChevronRight className="h-6 w-6 md:h-7 md:w-7" strokeWidth={2.5} />
            </Button>

            {/* Counter pill */}
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm font-medium px-3 py-1.5 rounded-full backdrop-blur z-10">
              {selectedImageIndex + 1} / {images.length}
            </span>
          </div>

          {/* Thumbnails - Horizontal scroll on mobile, sidebar on desktop */}
          <div className="md:absolute md:right-0 md:top-16 md:bottom-0 md:w-64 bg-black/50 p-2 md:p-4 overflow-x-auto md:overflow-y-auto shrink-0">
            <div className="flex md:grid md:grid-cols-2 gap-2">
              {images.map((img, index) => (
                <button
                  key={index}
                  className={`shrink-0 w-16 h-16 md:w-auto md:h-auto md:aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                    index === selectedImageIndex 
                      ? 'border-primary ring-2 ring-primary/50' 
                      : 'border-transparent hover:border-white/50'
                  }`}
                  onClick={() => setSelectedImageIndex(index)}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = defaultImages[0]; }}
                  />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
        <Link to="/listings">
          <Button variant="ghost" className="gap-2" data-testid="back-btn">
            <ArrowLeft className="h-4 w-4" />
            Retour
          </Button>
        </Link>
      </div>

      {/* Image Gallery - Mobile Optimized */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 mb-8">
        {/* Video Hero (Airbnb-style) — shown when listing has a video */}
        {hasVideo && (
          <ListingVideoPlayer
            videoUrl={listing.video_url}
            thumbnail={listing.video_thumbnail}
            duration={listing.video_duration}
            title={listing.title}
          />
        )}

        {/* Mobile Gallery - Single large image with thumbnails below */}
        <div className="md:hidden" data-testid="listing-gallery-mobile">
          <button 
            className="w-full relative rounded-xl overflow-hidden"
            onClick={() => { setSelectedImageIndex(0); setShowGallery(true); }}
          >
            <div className="aspect-video">
              <img
                src={images[0]}
                alt={`${listing.title} - 1`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.src = defaultImages[0]; }}
              />
            </div>
            <div className="absolute bottom-3 right-3 bg-black/70 text-white px-3 py-1.5 rounded-full text-sm font-medium flex items-center gap-1.5">
              <Eye className="h-4 w-4" />
              {images.length} photos
            </div>
          </button>
          {/* Mobile thumbnails row */}
          {images.length > 1 && (
            <div className="flex gap-2 mt-2 overflow-x-auto pb-2 scrollbar-hide">
              {images.slice(0, 6).map((img, index) => (
                <button
                  key={index}
                  className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                    index === 0 ? 'border-primary' : 'border-transparent'
                  }`}
                  onClick={() => { setSelectedImageIndex(index); setShowGallery(true); }}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.src = defaultImages[0]; }}
                  />
                </button>
              ))}
              {images.length > 6 && (
                <button
                  className="shrink-0 w-16 h-16 rounded-lg bg-muted flex items-center justify-center text-sm font-medium"
                  onClick={() => { setSelectedImageIndex(6); setShowGallery(true); }}
                >
                  +{images.length - 6}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Desktop Gallery Grid */}
        <div className="hidden md:grid grid-cols-4 gap-2 rounded-2xl overflow-hidden" data-testid="listing-gallery">
          {/* Main Large Image */}
          <button 
            className="col-span-2 row-span-2 relative group cursor-pointer"
            onClick={() => { setSelectedImageIndex(0); setShowGallery(true); }}
          >
            <div className="aspect-square">
              <img
                src={images[0]}
                alt={`${listing.title} - 1`}
                className="w-full h-full object-cover group-hover:brightness-90 transition-all"
                onError={(e) => { e.target.src = defaultImages[0]; }}
              />
            </div>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 text-white font-medium bg-black/50 px-4 py-2 rounded-full transition-all">
                Voir les photos
              </span>
            </div>
          </button>

          {/* Secondary Images */}
          {images.slice(1, 5).map((img, index) => (
            <button
              key={index + 1}
              className="relative group cursor-pointer"
              onClick={() => { setSelectedImageIndex(index + 1); setShowGallery(true); }}
            >
              <div className="aspect-square">
                <img
                  src={img}
                  alt={`${listing.title} - ${index + 2}`}
                  className="w-full h-full object-cover group-hover:brightness-90 transition-all"
                  onError={(e) => { e.target.src = defaultImages[0]; }}
                />
              </div>
              {/* Show "+X" on last visible image if there are more */}
              {index === 3 && images.length > 5 && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">+{images.length - 5}</span>
                </div>
              )}
            </button>
          ))}

          {/* Fill empty slots if less than 5 images */}
          {images.length < 5 && Array(5 - images.length).fill(null).map((_, index) => (
            <div key={`empty-${index}`} className="aspect-square bg-muted" />
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <Badge variant="secondary">
                  {typeLabels[listing.listing_type] || listing.listing_type}
                </Badge>
                {listing.is_featured && (
                  <Badge className="bg-accent text-accent-foreground gap-1">
                    <Star className="h-3 w-3" />
                    Featured
                  </Badge>
                )}
                {listing.is_verified && (
                  <Badge className="bg-primary text-primary-foreground">Vérifié</Badge>
                )}
              </div>
              
              <h1 className="font-heading text-2xl md:text-4xl font-bold mb-3" data-testid="listing-title">
                {listing.title}
              </h1>
              
              <div className="flex items-center gap-4 text-muted-foreground">
                <div className="flex items-center gap-1">
                  <MapPin className="h-5 w-5" />
                  <span>{listing.neighborhood}, {listing.city}, {listing.country}</span>
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="h-5 w-5" />
                  <span>{listing.views} {t('views')}</span>
                </div>
              </div>
            </div>

            {/* Quick Info */}
            <Card>
              <CardContent className="p-6">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('guests')}</p>
                      <p className="font-semibold">{listing.max_guests}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Home className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">{t('furnished')}</p>
                      <p className="font-semibold">{listing.is_furnished ? 'Oui' : 'Non'}</p>
                    </div>
                  </div>
                  {listing.has_wifi && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Wifi className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{t('wifi')}</p>
                      </div>
                    </div>
                  )}
                  {listing.has_ac && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Wind className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{t('ac')}</p>
                      </div>
                    </div>
                  )}
                  {listing.has_parking && (
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Car className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{t('parking')}</p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Description */}
            <div>
              <h2 className="font-heading text-xl font-semibold mb-4">{t('description')}</h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap" data-testid="listing-description">
                {listing.description}
              </p>
            </div>

            {/* Amenities */}
            {listing.amenities?.length > 0 && (
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4">{t('equipments')}</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {listing.amenities.map((amenity, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-primary" />
                      <span>{amenity}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rules */}
            {listing.rules && (
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4">{t('rules')}</h2>
                <p className="text-muted-foreground">{listing.rules}</p>
              </div>
            )}

            {/* Google Maps Location */}
            {listing.google_maps_url && (
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4">Localisation</h2>
                <a
                  href={listing.google_maps_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <MapPin className="h-5 w-5" />
                  Voir sur Google Maps
                </a>
              </div>
            )}
          </div>

          {/* Sidebar - Booking Card */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24" data-testid="booking-card">
              <CardContent className="p-6 space-y-6">
                {/* Price */}
                <div className="text-center pb-6 border-b border-border">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-primary">
                      {listing.price_per_night?.toLocaleString()}
                    </span>
                    <span className="text-muted-foreground">FCFA{t('perNight')}</span>
                  </div>
                </div>

                {/* Owner Info */}
                {listing.owner_name && (
                  <div className="flex items-center gap-3 pb-6 border-b border-border">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-semibold text-primary">
                        {listing.owner_name[0]?.toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold">{listing.owner_name}</p>
                      <p className="text-sm text-muted-foreground">Propriétaire</p>
                    </div>
                  </div>
                )}

                {/* WhatsApp Contact Button */}
                <a
                  href={getWhatsAppLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                  onClick={handleWhatsAppClick}
                  data-testid="whatsapp-contact-btn"
                >
                  <Button className="w-full h-14 text-lg gap-3 bg-green-500 hover:bg-green-600 rounded-full">
                    <MessageCircle className="h-6 w-6" />
                    {t('contactWhatsApp')}
                  </Button>
                </a>

                {/* Share Button — multi-channel (Web Share API / FB / X / WhatsApp / Copy link) */}
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleNativeShare}
                    className="w-full h-12 gap-3 rounded-full"
                    data-testid="share-btn"
                  >
                    <Share2 className="h-5 w-5" />
                    {language === 'fr' ? 'Partager cette annonce' : 'Share this listing'}
                  </Button>

                  {shareMenuOpen && (
                    <>
                      {/* Click-outside catcher */}
                      <button
                        type="button"
                        aria-label="close share menu"
                        className="fixed inset-0 z-40 bg-transparent cursor-default"
                        onClick={() => setShareMenuOpen(false)}
                      />
                      <div
                        role="menu"
                        className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-xl border shadow-lg overflow-hidden"
                        data-testid="share-menu"
                      >
                        <button
                          type="button"
                          onClick={shareToWhatsApp}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left"
                          data-testid="share-whatsapp"
                        >
                          <MessageCircle className="h-5 w-5 text-green-600" />
                          <span className="font-medium">WhatsApp</span>
                        </button>
                        <button
                          type="button"
                          onClick={shareToFacebook}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left border-t"
                          data-testid="share-facebook"
                        >
                          <Facebook className="h-5 w-5 text-blue-600" />
                          <span className="font-medium">Facebook</span>
                        </button>
                        <button
                          type="button"
                          onClick={shareToTwitter}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left border-t"
                          data-testid="share-twitter"
                        >
                          <Twitter className="h-5 w-5 text-sky-500" />
                          <span className="font-medium">X / Twitter</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleCopyLink}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/60 transition-colors text-left border-t"
                          data-testid="share-copy"
                        >
                          <Copy className="h-5 w-5 text-muted-foreground" />
                          <span className="font-medium">
                            {language === 'fr' ? 'Copier le lien' : 'Copy link'}
                          </span>
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {/* Phone */}
                {listing.whatsapp && (
                  <a href={`tel:${listing.whatsapp}`} className="block">
                    <Button variant="outline" className="w-full gap-2">
                      <Phone className="h-5 w-5" />
                      {listing.whatsapp}
                    </Button>
                  </a>
                )}

                {/* Disclaimer */}
                <p className="text-xs text-muted-foreground text-center">
                  {language === 'fr'
                    ? 'En contactant ce propriétaire, vous acceptez nos conditions d\'utilisation.'
                    : 'By contacting this owner, you agree to our terms of service.'}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Premium Airbnb-style video player: thumbnail with elegant play button,
// loads + plays inline only after user clicks (no aggressive autoplay).
const ListingVideoPlayer = ({ videoUrl, thumbnail, duration, title }) => {
  const [playing, setPlaying] = useState(false);

  if (!videoUrl) return null;

  return (
    <div className="mb-4 rounded-2xl overflow-hidden bg-black relative group" data-testid="listing-video-player">
      <div className="aspect-video w-full">
        {playing ? (
          <video
            src={videoUrl}
            poster={thumbnail}
            controls
            autoPlay
            playsInline
            preload="auto"
            className="w-full h-full object-contain bg-black"
            data-testid="listing-video-element"
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="relative w-full h-full block cursor-pointer focus:outline-none"
            aria-label={`Lire la vidéo de ${title || ''}`}
            data-testid="listing-video-play-btn"
          >
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={`Vidéo de ${title || 'l\'annonce'}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-900" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-white/95 shadow-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                <Play className="h-8 w-8 md:h-10 md:w-10 text-slate-900 ml-1" fill="currentColor" />
              </span>
            </div>
            {duration && (
              <span className="absolute bottom-3 right-3 text-xs md:text-sm font-medium text-white bg-black/60 px-2.5 py-1 rounded-full">
                {Math.round(duration)}s
              </span>
            )}
            <span className="absolute top-3 left-3 text-xs font-semibold uppercase tracking-wide text-white bg-black/60 px-2.5 py-1 rounded-full backdrop-blur">
              Vidéo
            </span>
          </button>
        )}
      </div>
    </div>
  );
};


export default ListingDetailPage;
